/**
 * A house number is a number with an optional short addition: "20", "20A", "20 bis", "20-2", "1-3".
 * Anything else — a street name, or a long value the browser cut off mid-word — is not a house
 * number, it is autofill spill.
 */
const HOUSE_NUMBER_PATTERN = /^\d{1,6}\s?[a-z]{0,3}(\s?[-/]\s?\d{0,6}\s?[a-z]{0,3})?$/i;

/** The number the street line already ends with, if it has one. */
const TRAILING_NUMBER = /(^|\s)(\d{1,6}[a-z]{0,3}(?:\s?[-/]\s?\d{0,6}[a-z]{0,3})?)$/i;

/** Does the street line carry a number of its own anywhere — not necessarily at the end? */
const CONTAINS_NUMBER = /(^|\s)\d/;

const leadingDigits = (value: string): string => {
    const match = value.match(/^\d+/);

    return match ? match[0] : '';
};

export const isHouseNumberLike = (value: string): boolean => HOUSE_NUMBER_PATTERN.test((value || '').trim());

/**
 * Join the street and the separate house number into the single address line the order API expects.
 *
 * Browser address autofill has no notion of a separate house number field, so it regularly drops the
 * complete street line into both inputs. Appending blindly then yields "Werktuigweg 20 20", which is
 * what Trespa ends up receiving.
 *
 * Comparing the two fields for equality is not enough: the inputs have different maxlengths (30 and
 * 10), so a spilled value is truncated at a different point in each and the two can never match. The
 * customer's own screenshot shows exactly that — "Wetering 20-Marketing Departme" alongside
 * "20-Marketi". So judge the house number on its shape instead, and only when the street line
 * already carries a number of its own — otherwise the house number field is the only place the
 * number can come from and dropping it would lose real data.
 */
export const composeAddressLine1 = (street: string, houseNumber: string): string => {
    const trimmedStreet = (street || '').trim();
    const trimmedHouseNumber = (houseNumber || '').trim();

    if (!trimmedHouseNumber) return trimmedStreet;
    if (!trimmedStreet) return trimmedHouseNumber;

    // Autofill spilled a street line into the house number field. Safe to drop only because the
    // street line already carries a number; otherwise this field is the only source of one.
    if (CONTAINS_NUMBER.test(trimmedStreet) && !isHouseNumberLike(trimmedHouseNumber)) return trimmedStreet;

    // Same number, but the user added a suffix the browser did not fill ("Werktuigweg 20" + "20A").
    const trailing = trimmedStreet.match(TRAILING_NUMBER);

    if (trailing && typeof trailing.index === 'number'
        && leadingDigits(trailing[2]) === leadingDigits(trimmedHouseNumber)) {
        return `${trimmedStreet.slice(0, trailing.index + trailing[1].length)}${trimmedHouseNumber}`;
    }

    return `${trimmedStreet} ${trimmedHouseNumber}`;
};

/**
 * Autofill also tends to put the city in the "additional address" input. That is never meaningful,
 * so drop the second address line when it only repeats the city.
 */
export const composeAddressLine2 = (addressLine2: string, city: string): string => {
    const trimmedAddressLine2 = (addressLine2 || '').trim();

    if (trimmedAddressLine2.toLowerCase() === (city || '').trim().toLowerCase()) return '';

    return trimmedAddressLine2;
};
